import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash-es';
import { BehaviorSubject, Subject } from 'rxjs';

import { getDuration, getMinutes } from './helpers';
import { ScheduleEntry } from './timemanager.models';

@Injectable()
export class PeriodService {
  programs$: BehaviorSubject<{ viewValue: string; value: string }[]> = new BehaviorSubject<
    { viewValue: string; value: string }[]
  >([]);

  defaultMode: string;

  multipleDaysSelection$: Subject<number[]> = new Subject<number[]>();

  recalculatePeriodsByProgramChange(
    scheduleEntries: ScheduleEntry[],
    scheduleEntry: ScheduleEntry
  ): ScheduleEntry[] {
    const entries = cloneDeep<ScheduleEntry[]>(scheduleEntries);

    let idx: number;
    let entry: ScheduleEntry;
    let mergedScheduleEntry: ScheduleEntry = scheduleEntry;

    // find updated scheduleEntry by position
    entry = entries.find((e) => e.position === scheduleEntry.position);
    idx = entries.indexOf(entry);

    if (entries[idx].duration === 0) {
      // in case of empty scheduleEntry
      entries.splice(idx, 1);
      scheduleEntry = entries[idx];
    }

    // update program
    entry.mode = scheduleEntry.mode;
    const prev = entries[idx - 1];

    // merge with prev one if a program is the same
    if (prev && prev.mode === scheduleEntry.mode) {
      mergedScheduleEntry = {
        position: scheduleEntry.position,
        start: prev.start,
        end: scheduleEntry.end,
        mode: scheduleEntry.mode,
        duration: prev.duration + scheduleEntry.duration,
      };

      // replace the prev scheduleEntry with merged one
      entries[idx - 1] = mergedScheduleEntry;

      // remove changed scheduleEntry
      entries.splice(idx, 1);
    }

    // find again in case it was merged with prev one
    entry = entries.find((e) => e.position === scheduleEntry.position);
    idx = entries.indexOf(entry);

    const next = entries[idx + 1];

    // merge with next one if a program is the same
    if (next && next.mode === scheduleEntry.mode) {
      mergedScheduleEntry = {
        position: scheduleEntry.position,
        start: entry.start,
        end: next.end,
        mode: scheduleEntry.mode,
        duration: entry.duration + next.duration,
      };

      // replace the current scheduleEntry with merged one
      entries[idx] = mergedScheduleEntry;

      // remove next scheduleEntry
      entries.splice(idx + 1, 1);
    }

    // update position
    entries.forEach((e, i) => {
      e.position = i;
    });

    return entries;
  }

  recalculatePeriodsByStartTimeChange(
    scheduleEntries: ScheduleEntry[],
    scheduleEntry: ScheduleEntry
  ): ScheduleEntry[] {
    const entries = cloneDeep<ScheduleEntry[]>(scheduleEntries);
    const defaultMode = this.defaultMode;
    let idx: number;
    let entry: ScheduleEntry;

    // find updated scheduleEntry by position
    entry = entries.find((e) => e.position === scheduleEntry.position);
    idx = entries.indexOf(entry);

    if (entries.length === 1 && scheduleEntry.mode !== defaultMode) {
      // update start time
      entry.start = scheduleEntry.start;
      const defaultScheduleEntry: ScheduleEntry = {
        position: 0,
        start: '00:00',
        end: scheduleEntry.start,
        mode: defaultMode,
      };
      const newEntries = [defaultScheduleEntry, entry];
      newEntries.forEach((e, i) => {
        e.position = i;
        e.duration = getDuration(e.start, e.end);
      });

      return newEntries;
    } else if (entries.length === 1 && scheduleEntry.mode === defaultMode) {
      return entries;
    }

    const min = getMinutes(scheduleEntry.start);

    if (min < getMinutes(entry.start)) {
      // update start time
      entry.start = scheduleEntry.start;

      const prevs = entries.slice(0, idx);

      let begining = [];
      let nexts;
      if (min > 0) {
        let prevId;
        prevs.forEach((p, i) => {
          if (min >= getMinutes(p.start) && min < getMinutes(p.end)) {
            prevId = i;
            p.end = scheduleEntry.start;
          }
        });
        begining = prevs.slice(0, prevId + 1);
      }

      if (min === getMinutes(entry.start)) {
        // when new program is begining in the same time as prevs ones
        let prevId;
        prevs.forEach((p, i) => {
          if (min >= getMinutes(p.start) && min <= getMinutes(p.end)) {
            prevId = i;
            p.end = scheduleEntry.start;
          }
        });

        begining = prevs.slice(0, prevId + 1);
      }

      nexts = entries.slice(idx, entries.length);
      const newEntries = [...begining, ...nexts];

      newEntries.forEach((e, i) => {
        e.position = i;
        e.duration = getDuration(e.start, e.end);
      });

      const newScheduleEntry = newEntries.find((e) => e.start === scheduleEntry.start);

      return this.recalculatePeriodsByProgramChange(newEntries, newScheduleEntry);
    } else {
      // update start time
      entry.start = scheduleEntry.start;

      const prev = entries[idx - 1];
      let begining: ScheduleEntry[];

      if (prev) {
        // prev.end = scheduleEntry.start;
        if (
          min >= getMinutes(prev.start) &&
          min < getMinutes(prev.end) &&
          prev.mode !== defaultMode
        ) {
          prev.end = scheduleEntry.start;
        }
        if (prev.mode === defaultMode) {
          prev.end = scheduleEntry.start;
        }
        begining = entries.slice(0, idx + 1);
      } else {
        begining = [
          {
            position: 0,
            start: '00:00',
            end: scheduleEntry.start,
            mode: defaultMode,
          },
          scheduleEntry,
        ];
      }

      const nexts = entries.slice(idx + 1, entries.length);

      let nextId = null;
      nexts.forEach((n, i) => {
        if (min >= getMinutes(n.start) && min < getMinutes(n.end)) {
          nextId = i;
          n.start = scheduleEntry.end;
        }
      });

      const end = nexts.slice(nextId, entries.length);
      const newEntries = [...begining, ...end];

      newEntries.forEach((e, i) => {
        e.position = i;
        e.duration = getDuration(e.start, e.end);
      });

      const newScheduleEntry = newEntries.find((e) => e.start === scheduleEntry.start);

      return this.recalculatePeriodsByProgramChange(newEntries, newScheduleEntry);
    }
  }

  recalculatePeriodsByEndTimeChange(
    scheduleEntries: ScheduleEntry[],
    scheduleEntry: ScheduleEntry
  ): ScheduleEntry[] {
    const entries = cloneDeep<ScheduleEntry[]>(scheduleEntries);
    const defaultMode = this.defaultMode;
    let idx: number;
    let entry: ScheduleEntry;

    // find updated scheduleEntry by position
    entry = entries.find((e) => e.position === scheduleEntry.position);
    idx = entries.indexOf(entry);

    if (entries.length === 1 && scheduleEntry.mode !== defaultMode) {
      // update end time
      entry.end = scheduleEntry.end;
      const defaultScheduleEntry: ScheduleEntry = {
        position: 1,
        start: scheduleEntry.end,
        end: '24:00',
        mode: defaultMode,
      };
      const newEntries = [entry, defaultScheduleEntry];
      newEntries.forEach((e, i) => {
        e.position = i;
        e.duration = getDuration(e.start, e.end);
      });

      return newEntries;
    } else if (entries.length === 1 && scheduleEntry.mode === defaultMode) {
      return entries;
    }

    const min = getMinutes(scheduleEntry.end);

    if (min > getMinutes(entry.end)) {
      // update end time
      entry.end = scheduleEntry.end;

      const begining = entries.slice(0, idx + 1);
      let nexts;

      nexts = entries.slice(idx + 1, entries.length);

      let nextId = nexts.length;
      nexts.forEach((n, i) => {
        if (min >= getMinutes(n.start) && min < getMinutes(n.end)) {
          nextId = i;
          n.start = scheduleEntry.end;
        }
      });
      const ends = nexts.slice(nextId, nexts.length);
      const newEntries = [...begining, ...ends];

      newEntries.forEach((e, i) => {
        e.position = i;
        e.duration = getDuration(e.start, e.end);
      });

      const newScheduleEntry = newEntries.find((e) => e.start === scheduleEntry.start);

      return this.recalculatePeriodsByProgramChange(newEntries, newScheduleEntry);
    } else {
      // update end time
      entry.end = scheduleEntry.end;

      const next = entries[idx + 1];
      let ends: ScheduleEntry[];
      if (next) {
        if (
          min >= getMinutes(next.start) &&
          min < getMinutes(next.end) &&
          next.mode !== defaultMode
        ) {
          next.start = scheduleEntry.end;
        }
        if (next.mode === defaultMode) {
          next.start = scheduleEntry.end;
        }
        ends = entries.slice(idx, entries.length);
      } else {
        ends = [
          scheduleEntry,
          {
            position: 0,
            start: scheduleEntry.end,
            end: '24:00',
            mode: defaultMode,
          },
        ];
      }

      const prevs = entries.slice(0, idx);

      let prevId;
      prevs.forEach((n, i) => {
        if (min > getMinutes(n.start) && min <= getMinutes(n.end)) {
          prevId = i;
          n.end = scheduleEntry.start;
        }
      });

      const begining = prevs.slice(0, prevId);
      const newEntries = [...begining, ...ends];

      newEntries.forEach((e, i) => {
        e.position = i;
        e.duration = getDuration(e.start, e.end);
      });

      const newScheduleEntry = newEntries.find((e) => e.start === scheduleEntry.start);

      return this.recalculatePeriodsByProgramChange(newEntries, newScheduleEntry);
    }
  }

  recalculatePeriodsByRemoveOne(
    scheduleEntries: ScheduleEntry[],
    scheduleEntry: ScheduleEntry
  ): ScheduleEntry[] {
    const entries = cloneDeep<ScheduleEntry[]>(scheduleEntries);
    const defaultMode = this.defaultMode;
    let idx: number;
    let entry: ScheduleEntry;
    let next: ScheduleEntry;
    let prev: ScheduleEntry;

    // find updated scheduleEntry by position
    entry = entries.find((e) => e.position === scheduleEntry.position);
    idx = entries.indexOf(entry);

    if (entries.length === 1) {
      if (scheduleEntry.mode !== defaultMode) {
        entry.mode = defaultMode;
      }

      return entries;
    }

    next = entries[idx + 1];
    prev = entries[idx - 1];

    if (next && prev) {
      if (next.mode === prev.mode) {
        scheduleEntry.mode = next.mode; // or defaultMode?
      } else if (prev.mode === defaultMode || next.mode === defaultMode) {
        scheduleEntry.mode = defaultMode;
      } else {
        scheduleEntry.mode = prev.mode;
      }
    } else if (next) {
      scheduleEntry.mode = next.mode;
    } else {
      // prev
      scheduleEntry.mode = prev.mode;
    }

    return this.recalculatePeriodsByProgramChange(entries, scheduleEntry);
  }

  recalculatePeriodsByAddOne(
    scheduleEntries: ScheduleEntry[],
    newScheduleEntry: any
  ): ScheduleEntry[] {
    const entries = cloneDeep<ScheduleEntry[]>(scheduleEntries);

    const prevs = entries.filter((e) => getMinutes(newScheduleEntry.start) > getMinutes(e.start));
    const nexts = entries.filter((e) => getMinutes(newScheduleEntry.end) < getMinutes(e.end));

    const newEntries = [...prevs, newScheduleEntry, ...nexts];
    const idx = newEntries.indexOf(newScheduleEntry);

    const prev = newEntries[idx - 1];
    if (prev) {
      newEntries[idx - 1] = {
        ...prev,
        end: newScheduleEntry.start,
      };
    }

    const next = newEntries[idx + 1];
    if (next) {
      newEntries[idx + 1] = {
        ...next,
        start: newScheduleEntry.end,
      };
    }

    newEntries.forEach((e, i) => {
      e.position = i;
      e.duration = getDuration(e.start, e.end);
    });

    return this.recalculatePeriodsByProgramChange(newEntries, cloneDeep(newScheduleEntry));
  }
}
